import { useQuery } from '@apollo/client';
import { Box } from '@mui/material';
import Loader from 'Components/Forms/Loader';
import useCompanyFeatureFlags from 'FeatureModules/Auth/hooks/useCompanyFeatureFlags';
import { useLogEventOnMount } from 'hooks/useLogEvent';
import { Tabs } from 'DesignSystem';
import { GET_FEEDBACK } from 'graphql/secure/queries';
import React, { FC } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PRODUCT_TABS, EProductTabKey } from './ProductOverview';
import { ECompanyFeature } from 'FeatureModules/Auth/types/company.types';
import ErrorState from 'Components/ErrorState';
import FeatureOverlay from 'Components/FeatureOverlay';
import PageContainer from 'Components/PageContainer';
import PageTitle from 'Components/PageTitle';
import FeedbackDashboard from '../Components/Feedback/FeedbackDashboard';

const FeedbackAnalytics: FC = () => {
  const { id } = useParams();

  const navigate = useNavigate();

  useLogEventOnMount('VIEW_PRODUCT_FEEDBACK', { productId: id });

  const { isCompanyFeatureEnabled } = useCompanyFeatureFlags();
  const feedbackFeatureEnabled = isCompanyFeatureEnabled(ECompanyFeature.FEEDBACK);

  const handleChangeTab = (tab: EProductTabKey) => {
    if (tab === EProductTabKey.OVERVIEW) {
      navigate(`/products/active/${id}`);
    } else {
      navigate(`/products/active/${id}/${tab}`);
    }
  };

  const { error, loading, data } = useQuery(GET_FEEDBACK, {
    variables: { id },
  });

  if (error) return <ErrorState />;
  if (!data && loading) return <Loader />;

  const {
    product,
    productFeedback: { reactions },
  } = data;

  return (
    <PageContainer>
      <PageTitle
        title="Product feedback"
        titleExtension={product?.title}
        goBackLabel="Product pages"
        goBackUrl="/products/active"
      />

      <Box mt={3} mb={2}>
        <Tabs
          tabs={PRODUCT_TABS}
          selectedTab={EProductTabKey.FEEDBACK}
          onChange={handleChangeTab}
        />
      </Box>

      {!feedbackFeatureEnabled && <FeatureOverlay />}
      {!reactions?.edges.length ? (
        <ErrorState error="You have not received any feedback yet." />
      ) : (
        <FeedbackDashboard
          productFeedbackAnalytics={data.productFeedbackAnalytics}
          reactions={reactions}
        />
      )}
    </PageContainer>
  );
};

export default FeedbackAnalytics;
